import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Banknote,
  FileText,
  HandCoins,
  Info,
  Mail,
  Receipt,
} from "lucide-react";
import {
  Alert,
  Badge,
  brandFillClass,
  type IconTileTone,
} from "@/components/ui";
import {
  EntityHeaderLayout,
  StatCard,
  StatCardGrid,
  selectableStatCardButtonClass,
} from "@/components/common";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";
import { cn, getInitials } from "@/lib/utils";
import type {
  SettlementFlowKind,
  SettlementPayeeRow,
} from "@/services/adminSettlementService";
import { formatMoney } from "@/lib/money";
import {
  settlementRoleLabel,
  type SettlementMetricFilter,
} from "../settlementUi";
import { StatusBadge } from "../shared";

const METRIC_ARIA_KEYS: Record<SettlementMetricFilter, string> = {
  payable: "settlements.detail.selectMetricPayable",
  invoiceable: "settlements.detail.selectMetricInvoiceable",
  collectible: "settlements.detail.selectMetricCollectible",
  supplierInvoice: "settlements.detail.selectMetricSupplierInvoice",
};

export function SettlementPayeeHeader({
  payee,
  flow,
  activeMetric,
  onSelectMetric,
  actions,
}: {
  payee: SettlementPayeeRow;
  actions?: ReactNode;
  flow: SettlementFlowKind | null;
  activeMetric: SettlementMetricFilter | null;
  onSelectMetric: (metric: SettlementMetricFilter) => void;
}) {
  const { t } = useTranslation("payments");

  const metrics: {
    id: SettlementMetricFilter;
    title: string;
    value: string;
    icon: typeof Banknote;
    iconColor: IconTileTone;
  }[] = [
    {
      id: "payable",
      title: t("settlements.detail.openPayable"),
      value: formatMoney(payee.open_payable_cents, payee.currency),
      icon: Banknote,
      iconColor: "primary",
    },
    {
      id: "invoiceable",
      title: t("settlements.detail.openInvoiceable"),
      value: formatMoney(payee.open_invoiceable_cents, payee.currency),
      icon: FileText,
      iconColor: "accent",
    },
    {
      id: "collectible",
      title: t("settlements.detail.openCollectible"),
      value: formatMoney(payee.open_collectible_cents, payee.currency),
      icon: HandCoins,
      iconColor: "warning",
    },
    {
      id: "supplierInvoice",
      title: t("settlements.detail.openPendingSupplierInvoice"),
      value: formatMoney(
        payee.open_pending_supplier_invoice_cents,
        payee.currency,
      ),
      icon: Receipt,
      iconColor: "success",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <EntityHeaderLayout
        className="animate-slide-up mb-0 overflow-hidden"
        icon={
          <div
            className={cn("flex h-14 w-14 items-center justify-center rounded-xl text-lg font-semibold shadow-sm", brandFillClass("primary"))}
            aria-hidden
          >
            {getInitials(payee.name) || "?"}
          </div>
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-surface-900 text-2xl font-semibold tracking-tight text-balance sm:text-[1.75rem] sm:leading-tight">
              {payee.name}
            </h1>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Badge
                variant="primary"
                size="sm"
              >
                {settlementRoleLabel(payee.role, t)}
              </Badge>
              {flow && (
                <StatusBadge
                  status={flow}
                  label={t(`settlements.detail.flowLabel.${flow}`)}
                />
              )}
              {payee.region_code ? (
                <RegionCodeBadge code={payee.region_code} />
              ) : null}
              {!payee.has_legal_info && (
                <Badge
                  variant="warning"
                  size="sm"
                  dot
                >
                  {t("settlements.payees.legalIncompleteTooltip")}
                </Badge>
              )}
            </div>
            <p className="text-surface-500 mt-3 flex items-center gap-1.5 text-[13px]">
              <Mail
                className="text-surface-400 h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
              <span className="truncate">{payee.email}</span>
            </p>
            {flow && (
              <p className="text-surface-500 mt-1.5 flex items-start gap-1.5 text-[13px]">
                <Info
                  className="text-accent-500 mt-0.5 h-3.5 w-3.5 shrink-0"
                  aria-hidden
                />
                <span>{t(`settlements.detail.flowHint.${flow}`)}</span>
              </p>
            )}
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </div>
      </EntityHeaderLayout>

      <StatCardGrid layout="1-2-4" className="grid-cols-2">
        {metrics.map((metric) => {
          const isActive = activeMetric === metric.id;
          return (
            <button
              key={metric.id}
              type="button"
              aria-label={t(METRIC_ARIA_KEYS[metric.id])}
              aria-pressed={isActive}
              onClick={() => onSelectMetric(metric.id)}
              className={selectableStatCardButtonClass}
            >
              <StatCard
                title={metric.title}
                value={metric.value}
                icon={metric.icon}
                iconColor={metric.iconColor}
                selected={isActive}
                className="cursor-pointer"
              />
            </button>
          );
        })}
      </StatCardGrid>
    </div>
  );
}

export function MixedSelectionWarning() {
  const { t } = useTranslation("payments");

  return (
    <Alert
      variant="warning"
      className="shadow-xs"
    >
      {t("settlements.detail.mixedSelectionWarning")}
    </Alert>
  );
}
